'use client';

import { useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { describeCelebration, type CelebrationEvent } from './celebration-events';
import { fireBurst, fireSideCannons, fireGoldRain } from './confetti';

interface CelebrationModalProps {
  event: CelebrationEvent | null;
  open: boolean;
  onClose: () => void;
  onCta: (event: CelebrationEvent) => void;
}

export function CelebrationModal({ event, open, onClose, onCta }: CelebrationModalProps) {
  const copy = event ? describeCelebration(event) : null;

  useEffect(() => {
    if (!open || !event || !copy) return;
    if (copy.tone !== 'happy') return;
    if (event.kind === 'level_up') {
      fireGoldRain();
      setTimeout(fireSideCannons, 250);
    } else if (event.kind === 'badge_earned' || event.kind === 'certificate_received') {
      fireBurst();
    } else if (event.kind === 'rank_climbed' || event.kind === 'training_completed' || event.kind === 'streak_milestone') {
      fireSideCannons();
    } else if (event.kind === 'points_awarded' && 'points' in event && event.points >= 50) {
      fireBurst();
    }
  }, [open, event, copy]);

  if (!event || !copy) return null;
  const Icon = copy.icon;
  const isHappy = copy.tone === 'happy';
  const featuredImage = getFeaturedImage(event);
  const featuredKind = getFeaturedKind(event);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" showCloseButton>
        <div className={cn('h-2 w-full bg-gradient-to-r', copy.accent.split(' ').filter((c) => c.startsWith('from-') || c.startsWith('to-')).join(' '))} />
        <div className="px-6 pt-8 pb-6 text-center">
          {featuredImage ? (
            <div className="relative mx-auto mb-5 inline-block">
              <div className={cn(
                'absolute inset-0 -m-3 blur-2xl opacity-60 rounded-full bg-gradient-to-br',
                copy.accent
              )} />
              {featuredKind === 'course' ? (
                <img
                  src={featuredImage}
                  alt=""
                  className="relative w-40 h-24 object-cover rounded-xl border border-border shadow-lg"
                />
              ) : (
                <img
                  src={featuredImage}
                  alt=""
                  className={cn(
                    'relative w-28 h-28 object-contain drop-shadow-[0_8px_20px_rgba(34,197,94,0.35)]',
                    featuredKind === 'certificate' && 'rounded-xl border border-border bg-muted p-2 w-32 h-24 object-cover'
                  )}
                />
              )}
            </div>
          ) : (
            <div className={cn(
              'mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br',
              copy.accent
            )}>
              <Icon className="h-10 w-10" strokeWidth={1.8} />
            </div>
          )}
          <DialogTitle className="text-2xl font-bold tracking-tight mb-2">
            {copy.headline}
          </DialogTitle>
          <DialogDescription asChild>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {renderBold(copy.body)}
            </p>
          </DialogDescription>
          {copy.metric && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-muted/60 px-3 py-1 text-xs font-medium text-foreground">
              {copy.metric}
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex items-center justify-center gap-3">
          {copy.secondaryLabel && (
            <Button variant="ghost" onClick={onClose} className="min-w-24">
              {copy.secondaryLabel}
            </Button>
          )}
          <Button
            onClick={() => onCta(event)}
            className={cn(
              'min-w-32',
              isHappy ? '' : 'bg-amber-500 hover:bg-amber-600 text-white'
            )}
          >
            {copy.ctaLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

function getFeaturedImage(event: CelebrationEvent): string | undefined {
  switch (event.kind) {
    case 'badge_earned':
      return event.badgeIcon;
    case 'level_up':
      return event.levelIcon;
    case 'certificate_received':
      return event.certificateImage;
    case 'training_completed':
    case 'training_assigned':
    case 'training_due_soon':
    case 'training_expired':
      return event.courseImage;
    default:
      return undefined;
  }
}

function getFeaturedKind(event: CelebrationEvent): 'badge' | 'certificate' | 'course' | 'level' | undefined {
  switch (event.kind) {
    case 'badge_earned':
      return 'badge';
    case 'level_up':
      return 'level';
    case 'certificate_received':
      return 'certificate';
    case 'training_completed':
    case 'training_assigned':
    case 'training_due_soon':
    case 'training_expired':
      return 'course';
    default:
      return undefined;
  }
}
